import React, { FC, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import { Modal, Loader, SMBSelect } from '../../../components';
import { Close, Save } from '@mui/icons-material';
import {
  Fade,
  Button,
  Grid,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton
} from '@mui/material';
import { IAuthorityDetail, IAuthorityType } from '../../../models';
import { getStates, updateAuthority, createAuthority, getAuthorityTypes } from '../../../fetch';
import { formatMoney } from '../../../helpers';

interface IAuthorityModal {
  open: boolean;
  onClose: () => void;
  currentAuthority?: IAuthorityDetail;
  fetchAuthorities: () => {};
}

const AuthoritySchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required'),
  type: Yup.string().max(255, 'Max 255 characters').required('Required'),
  fee: Yup.string().max(255, 'Max 255 characters').required('Required'),
  phoneNumber: Yup.string().max(255, 'Max 255 characters'),
  addressLine1: Yup.string().max(255, 'Max 255 characters'),
  addressLine2: Yup.string().max(255, 'Max 255 characters'),
  city: Yup.string().max(255, 'Max 255 characters'),
  stateId: Yup.string().max(255, 'Max 255 characters'),
  zip: Yup.string()
});

export const AuthorityModal: FC<IAuthorityModal> = ({
  open,
  onClose,
  currentAuthority,
  fetchAuthorities
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [states, setStates] = useState([]);
  const [types, setTypes] = useState<IAuthorityType[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [actualFee, setActualFee] = useState<number>(0);

  const fetchStates = async () => {
    try {
      setIsLoadingStates(true);
      const res = await getStates();
      setStates(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const fetchTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const res = await getAuthorityTypes();
      setTypes(res);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchTypes();
  }, []);

  useEffect(() => {
    if (currentAuthority) {
      setActualFee(currentAuthority.fee);
    }
  }, [currentAuthority]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentAuthority?.name ?? '',
          type: currentAuthority?.type ?? '',
          fee: currentAuthority?.fee ? formatMoney(currentAuthority.fee) : '',
          addressLine1: currentAuthority?.addressLine1 ?? '',
          addressLine2: currentAuthority?.addressLine2 ?? '',
          city: currentAuthority?.city ?? '',
          stateId: currentAuthority?.stateId ?? '',
          zip: currentAuthority?.zip ?? '',
          phoneNumber: currentAuthority?.phoneNumber ?? ''
        }}
        validationSchema={AuthoritySchema}
        onSubmit={async (values, actions) => {
          try {
            const data = {
              ...values,
              fee: actualFee ? Number(actualFee) : 0,
              stateId: values.stateId ? Number(values.stateId) : null,
              zip: values.zip ? Number(values.zip) : null,
              phoneNumber: values.phoneNumber ? Number(values.phoneNumber) : null
            };
            if (currentAuthority && currentAuthority.authorityId) {
              await updateAuthority(currentAuthority.authorityId, {
                ...data,
                isActive: true
              });
            } else {
              await createAuthority(data);
            }
            enqueueSnackbar(currentAuthority ? 'Authority Updated!' : 'Authority Created!', {
              variant: 'success'
            });
            onClose();
            fetchAuthorities();
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Error saving authority.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                }
              }}
              maxWidth="md"
              title={currentAuthority ? `Edit Authority` : 'Add Authority'}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Name"
                          name="name"
                          value={values.name}
                          onBlur={handleBlur}
                          size="small"
                          required
                          error={touched.name && errors && errors.name ? true : false}
                          helperText={touched.name && errors && errors.name}
                          onChange={e => setFieldValue('name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          size="small"
                          required
                          error={touched.type && errors && errors.type ? true : false}
                        >
                          <SMBSelect
                            name="type"
                            disabled={isLoadingTypes}
                            label="Type"
                            id="type"
                            value={values.type}
                            onBlur={handleBlur}
                            variant="outlined"
                            handleClear={() => setFieldValue('type', '')}
                            onChange={({ target: { value } }) => {
                              setFieldValue('type', value);
                            }}
                          >
                            {types &&
                              types.map((type, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={type.value}>
                                    {type.description}
                                  </MenuItem>
                                );
                              })}
                          </SMBSelect>
                        </FormControl>
                        {touched.type && errors && errors.type && (
                          <FormHelperText sx={{ marginLeft: '16px' }} error>
                            {errors.type}
                          </FormHelperText>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          required
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Fee"
                          name="fee"
                          size="small"
                          error={touched.fee && errors && errors.fee ? true : false}
                          helperText={touched.fee && errors && errors.fee}
                          value={values.fee}
                          onBlur={e => {
                            const re = /^[+-]?(\d*\.)?\d+$/;
                            // Need to strip out all non numeric values from formatted value
                            const value = e.target.value.replace('$', '').replace(',', '');
                            // make sure it is number only before we update state
                            if (value === '' || re.test(value)) {
                              setActualFee(Number(value));
                            }
                            setFieldValue('fee', formatMoney(e.target.value, 2));
                          }}
                          onChange={e => {
                            setFieldValue('fee', e.target.value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Phone Number"
                          name="phone"
                          value={values.phoneNumber}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => {
                            const re = /^[0-9\b]+$/;
                            // if value is not blank, then test the regex
                            if (e.target.value === '' || re.test(e.target.value)) {
                              setFieldValue('phoneNumber', e.target.value);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Address Line 1"
                          name="addressLine1"
                          value={values.addressLine1}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('addressLine1', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Address Line 2"
                          name="addressLine2"
                          value={values.addressLine2}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('addressLine2', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="City"
                          name="city"
                          value={values.city}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('city', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel id="stateId">State</InputLabel>
                          <Select
                            disabled={isLoadingStates}
                            name="stateId"
                            labelId="stateId"
                            id="stateId"
                            value={values.stateId}
                            onChange={({ target: { value } }) => {
                              setFieldValue('stateId', value);
                            }}
                            endAdornment={
                              !!values.stateId && (
                                <InputAdornment
                                  position="end"
                                  sx={{
                                    position: 'absolute',
                                    right: 28,
                                    cursor: 'pointer'
                                  }}
                                >
                                  <IconButton
                                    disabled={isLoadingStates}
                                    sx={{ padding: 0 }}
                                    title="Clear"
                                    onClick={() => setFieldValue('stateId', '')}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          >
                            {states &&
                              states.map((state, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={state.stateId}>
                                    {state.name}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="ZIP"
                          name="zip"
                          value={values.zip}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => {
                            const re = /^[0-9\b]+$/;
                            // if value is not blank, then test the regex
                            if (e.target.value === '' || re.test(e.target.value)) {
                              setFieldValue('zip', e.target.value);
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<Close />}
                      color="inherit"
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
